"use client";

import { Loader2 } from "lucide-react";
import { useTheme } from "next-themes";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string; select_by?: string }) => void;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
            context?: string;
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: {
              type?: "standard" | "icon";
              theme?: "outline" | "filled_blue" | "filled_black";
              size?: "large" | "medium" | "small";
              text?: "signin_with" | "signup_with" | "continue_with" | "signin";
              shape?: "rectangular" | "pill" | "circle" | "square";
              logo_alignment?: "left" | "center";
              width?: number | string;
              locale?: string;
            },
          ) => void;
          prompt: (notification?: (notification: {
            isNotDisplayed: () => boolean;
            isSkippedMoment: () => boolean;
            isDismissedMoment: () => boolean;
            getDismissedReason: () => string;
            getNotDisplayedReason: () => string;
            getSkippedReason: () => string;
          }) => void) => void;
          disableAutoSelect: () => void;
        };
      };
    };
  }
}

export function GoogleIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
        fill="#EA4335"
      />
    </svg>
  );
}

interface GoogleSignInButtonProps {
  mode: "login" | "signup";
  onCredential: (credential: string) => void;
  loading?: boolean;
  disabled?: boolean;
  onError?: (message: string) => void;
}

export function GoogleSignInButton({
  mode,
  onCredential,
  loading = false,
  disabled = false,
  onError,
}: GoogleSignInButtonProps) {
  const { resolvedTheme, theme } = useTheme();
  const googleBtnContainerRef = useRef<HTMLDivElement>(null);
  const [gisMounted, setGisMounted] = useState(false);

  const clientId = (import.meta.env.VITE_GOOGLE_CLIENT_ID ?? "").trim();
  const isDark = resolvedTheme === "dark" || theme === "dark";

  useEffect(() => {
    if (!clientId) {
      setGisMounted(false);
      return;
    }

    let isSubscribed = true;

    function initAndRender() {
      if (!isSubscribed || !window.google?.accounts?.id || !googleBtnContainerRef.current) {
        return false;
      }

      try {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => {
            if (response.credential) {
              onCredential(response.credential);
            } else {
              const err = "No credential received from Google sign-in.";
              onError?.(err);
              toast.error(err);
            }
          },
          auto_select: false,
          cancel_on_tap_outside: true,
        });

        googleBtnContainerRef.current.innerHTML = "";

        // Render official Google Identity Services Button
        window.google.accounts.id.renderButton(googleBtnContainerRef.current, {
          type: "standard",
          theme: isDark ? "filled_black" : "outline",
          size: "large",
          text: mode === "signup" ? "signup_with" : "continue_with",
          shape: "rectangular",
          logo_alignment: "left",
          width: 384,
        });

        setGisMounted(true);
        return true;
      } catch (e) {
        console.error("Error initializing Google Identity Services:", e);
        return false;
      }
    }

    if (!initAndRender()) {
      const interval = setInterval(() => {
        if (initAndRender()) {
          clearInterval(interval);
        }
      }, 300);

      const timeout = setTimeout(() => {
        clearInterval(interval);
      }, 5000);

      return () => {
        isSubscribed = false;
        clearInterval(interval);
        clearTimeout(timeout);
      };
    }

    return () => {
      isSubscribed = false;
    };
  }, [clientId, isDark, mode, onCredential, onError]);

  function handleFallbackClick() {
    if (!clientId) {
      toast.error(
        "Google Sign-In is not configured. Please define VITE_GOOGLE_CLIENT_ID in your environment.",
      );
      return;
    }

    if (window.google?.accounts?.id) {
      window.google.accounts.id.prompt((notification) => {
        if (notification.isDismissedMoment()) {
          const reason = notification.getDismissedReason();
          if (reason !== "credential_returned") {
            toast.info("Google Sign-In cancelled.");
          }
        } else if (notification.isNotDisplayed()) {
          const reason = notification.getNotDisplayedReason();
          toast.info(`Google Sign-In prompt not displayed (${reason}).`);
        }
      });
    } else {
      toast.info("Google Identity Services is loading. Please try again in a moment.");
    }
  }

  return (
    <div className="relative w-full">
      {/* Official GIS rendered button container */}
      <div
        ref={googleBtnContainerRef}
        className={`w-full flex justify-center overflow-hidden rounded-xl ${
          gisMounted && !loading ? "block" : "hidden"
        }`}
        style={{ minHeight: "44px" }}
      />

      {/* Styled Fallback / Loading button matching the application UI */}
      {(!gisMounted || loading) && (
        <Button
          type="button"
          variant="outline"
          disabled={loading || disabled}
          onClick={handleFallbackClick}
          className="w-full h-11 rounded-xl font-semibold border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/5 flex items-center justify-center gap-2.5 transition-colors"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin text-[#087DB5]" />
          ) : (
            <GoogleIcon className="h-4 w-4 shrink-0" />
          )}
          <span>
            {loading
              ? "Authenticating with Google..."
              : mode === "signup"
              ? "Sign up with Google"
              : "Continue with Google"}
          </span>
        </Button>
      )}
    </div>
  );
}
